import styles from './form.module.css'

import React, { useEffect, useState } from 'react'

import {
  createEmailGroup,
  loadEmailGroups,
  updateEmailGroup,
} from '@disco/state-management/actions/EmailGroupActions'
import { updateBusinessSettings } from '@disco/state-management/actions/UserActions'
import { getEmailGroup } from '@disco/state-management/selectors/emailGroups'

import type { RecipientList } from 'types/list'

import BusinessAddressField from 'components/common/business-address-field'
import HelpTooltip from 'components/common/tooltip'
import { Box, Button, Flex, Text, TextField } from 'components/ds'
import Skeleton from 'components/ui/skeleton'

import { useDispatch, useSelector } from 'hooks/use-redux'

type DraftJsDoc = Record<string, any>

type FieldErrors = Partial<
  Record<'name' | 'sender_name' | 'sender_email' | 'business_address', string>
>

type FieldsProps = {
  list: RecipientList | null
  onClose: () => void
}

const FieldLabel = ({
  htmlFor,
  caption,
  tooltipId,
}: {
  htmlFor: string
  caption: string
  tooltipId?: string
}) => (
  <Flex gap="1" align="center" mb="1">
    <Text size="2" weight="medium" as="label" htmlFor={htmlFor}>
      {caption}
    </Text>
    {tooltipId && <HelpTooltip id={tooltipId} />}
  </Flex>
)

const MailingListFormFields = ({ list, onClose }: FieldsProps) => {
  const dispatch = useDispatch()
  const user = useSelector(state => state.user)
  const isSaving = useSelector(state => state.emailGroups.isSaving)

  const [name, setName] = useState(list?.name ?? '')
  const [senderName, setSenderName] = useState(
    list?.sender_name || user.first_name || '',
  )
  const [senderEmail, setSenderEmail] = useState(
    list?.sender_email || user.email || '',
  )
  const [businessAddress, setBusinessAddress] = useState<
    DraftJsDoc | null | undefined
  >(user.business?.business_address)
  const [businessAddressMjml, setBusinessAddressMjml] = useState<
    string | null | undefined
  >(user.business?.business_address_mjml)
  // '_' filler: an untouched, pre-existing address counts as filled
  const [businessAddressText, setBusinessAddressText] = useState('_')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitError, setSubmitError] = useState(false)

  const handleBusinessAddressChange = (val, _valHtml, valMjml, valText) => {
    setErrors({})
    setBusinessAddress(val)
    setBusinessAddressMjml(valMjml)
    setBusinessAddressText(valText ?? '')
  }

  const validate = (): FieldErrors => {
    const nextErrors: FieldErrors = {}
    if (!name.trim()) nextErrors.name = 'Please enter a name'
    else if (name.length > 100)
      nextErrors.name = 'Please use 100 characters or fewer'
    if (!senderName.trim())
      nextErrors.sender_name = 'Please enter a sender name'
    if (!senderEmail.trim())
      nextErrors.sender_email = 'Please enter a sender address'
    if (!businessAddressText.trim())
      nextErrors.business_address = 'Please enter a business address'
    return nextErrors
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (isSaving) return

    const nextErrors = validate()
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    setSubmitError(false)
    try {
      if (
        user.permissions?.includes('can_change_business_settings') &&
        user.business?.business_address_mjml !== businessAddressMjml
      ) {
        await dispatch(
          updateBusinessSettings(
            {
              business_address: businessAddress,
              // careful: html (business_address_mjml) is stored as "mjml"
              // generally this is not correct: html has to be wrapped in mjml tag
              business_address_mjml: businessAddressMjml,
            },
            false,
          ),
        )
      }

      const attrs: Partial<RecipientList> = {
        name,
        sender_name: senderName,
        sender_email: senderEmail,
      }

      if (list) {
        attrs.id = list.id
        attrs.recipients = list.recipients
        await dispatch(updateEmailGroup(attrs))
      } else {
        await dispatch(createEmailGroup(attrs))
      }
      onClose()
    } catch {
      setSubmitError(true)
    }
  }

  const clearErrorsAnd =
    <T,>(setter: (_value: T) => void) =>
    (value: T) => {
      setErrors({})
      setter(value)
    }

  return (
    <form onSubmit={handleSubmit}>
      <Box mb="4">
        <FieldLabel htmlFor="mailing-list-name" caption="Mailing list name" />
        <TextField.Root
          id="mailing-list-name"
          name="name"
          size="2"
          value={name}
          color={errors.name ? 'red' : undefined}
          onChange={event => clearErrorsAnd(setName)(event.target.value)}
          required
        />
        {errors.name && (
          <Text size="1" color="red" mt="1" as="div">
            {errors.name}
          </Text>
        )}
      </Box>
      <Box mb="4">
        <FieldLabel
          htmlFor="mailing-list-sender-name"
          caption="Sender display name"
          tooltipId="tooltip_campaigns_sender_name"
        />
        <TextField.Root
          id="mailing-list-sender-name"
          name="sender_name"
          size="2"
          placeholder={user.first_name}
          value={senderName}
          color={errors.sender_name ? 'red' : undefined}
          onChange={event => clearErrorsAnd(setSenderName)(event.target.value)}
        />
        {errors.sender_name && (
          <Text size="1" color="red" mt="1" as="div">
            {errors.sender_name}
          </Text>
        )}
      </Box>
      <Box mb="4">
        <FieldLabel
          htmlFor="mailing-list-sender-email"
          caption="Sender from address"
          tooltipId="tooltip_campaigns_sender_email"
        />
        <TextField.Root
          id="mailing-list-sender-email"
          name="sender_email"
          size="2"
          placeholder={user.email}
          value={senderEmail}
          color={errors.sender_email ? 'red' : undefined}
          onChange={event => clearErrorsAnd(setSenderEmail)(event.target.value)}
        />
        {errors.sender_email && (
          <Text size="1" color="red" mt="1" as="div">
            {errors.sender_email}
          </Text>
        )}
      </Box>
      <Box mb="4">
        <FieldLabel
          htmlFor="mailing-list-business-address"
          caption="Business address"
          tooltipId="tooltip_campaigns_business_address"
        />
        <div
          id="mailing-list-business-address"
          className={styles.BusinessAddressField}
        >
          <BusinessAddressField
            invalid={!!errors.business_address}
            initialContent={businessAddress}
            onChange={handleBusinessAddressChange}
          />
        </div>
        {errors.business_address && (
          <Text size="1" color="red" mt="1" as="div">
            {errors.business_address}
          </Text>
        )}
      </Box>

      {submitError && (
        <Text size="2" color="red" mb="3" as="div">
          Failed to save
        </Text>
      )}
      <Flex gap="3" mt="5" justify="end">
        <Button
          type="button"
          variant="soft"
          color="gray"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button type="submit" loading={isSaving}>
          Save
        </Button>
      </Flex>
    </form>
  )
}

type Props = {
  listId?: number
  onClose: () => void
}

/**
 * Create/edit mailing list form. While editing, waits for the list to load
 * (deep links can land here before email groups are fetched) and only then
 * mounts the fields so they're seeded with the list's values.
 */
const MailingListForm = ({ listId, onClose }: Props): React.ReactElement => {
  const dispatch = useDispatch()
  const emailGroups = useSelector(state => state.emailGroups)
  const list = useSelector(state =>
    listId ? getEmailGroup(state, { listId }) : null,
  )

  useEffect(() => {
    dispatch(loadEmailGroups())
  }, [dispatch])

  if (listId && !list) {
    if (emailGroups.isFetching || !emailGroups.isFetched) {
      return (
        <Flex
          direction="column"
          gap="4"
          data-testid="mailing-list-form-skeleton"
        >
          <Skeleton type="block" lines={2} />
          <Skeleton type="block" lines={2} />
          <Skeleton type="block" lines={2} />
          <Skeleton type="block" lines={3} />
        </Flex>
      )
    }
    return (
      <Text size="2" color="gray" as="p">
        This mailing list could not be found.
      </Text>
    )
  }

  return (
    <MailingListFormFields
      key={list?.id ?? 'create'}
      list={list ?? null}
      onClose={onClose}
    />
  )
}

export default MailingListForm
